import { BaseToggleTextBoxComponent } from './../base/base-toggle-text-box/base-toggle-text-box.component';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild, output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { merge, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { BasePropertyComponent } from '../base-property-component';
import { BaseSecurityTextBoxComponent } from '../base/base-security-text-box/base-security-text-box.component';
import { PopperHelper } from '@nts/std/utility';
import { AsyncPipe } from '@angular/common';
import { ViewReadyCoordinator } from '../view-ready-coordinator';

@UntilDestroy()
@Component({
    selector: 'nts-toggle-text-box',
    templateUrl: './toggle-text-box.component.html',
    styleUrls: ['./toggle-text-box.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    BaseToggleTextBoxComponent,
    BaseSecurityTextBoxComponent,
    AsyncPipe
]
})
export class ToggleTextBoxComponent extends BasePropertyComponent<boolean> implements OnInit, OnChanges, AfterViewInit {

    @Input() isDisabled: boolean = false;
    @Input() isReadOnly = false;
    @Input() showErrorTooltip: boolean = true;
    @Input() showErrorBorder: boolean = true;
    @Input() showTitleTooltip = true;
    @Input() showLabelText = true;
    @Input() tabIndex: number;
    @Input() primaryColor: string = null;

    @Output() onClick = new EventEmitter<void>();
    readonly viewReady = output<void>();
    isViewReady = false;

    @ViewChild('baseToggleTextBox', { static: false }) baseToggleTextBox: BaseToggleTextBoxComponent;
    @ViewChild('baseSecurityTextBox', { static: false }) baseSecurityTextBox: BaseSecurityTextBoxComponent;

    checkStatus: boolean;
    valueChanged$: Subject<boolean> = new Subject();
    private readonly viewReadyCoordinator = new ViewReadyCoordinator(['self', 'child'] as const, () => this.emitViewReady());

    get input(): HTMLInputElement {
        return (
            this.propertyViewModel?.securityAccess == null
                ? this.baseToggleTextBox.input
                : this.baseSecurityTextBox.input
        ) as HTMLInputElement;
    }

    untilDestroyedThis(): <U>(source: Observable<U>) => Observable<U> {
        return untilDestroyed<this>(this);
    }

    override ngOnChanges(): void {
        this.valueChanged$.next(true);

        // Rimane in ascolto quando cambia il pvm e il valore
        merge(
            this.propertyViewModel.customGetterValueChanged,
            this.propertyViewModel.propertyViewModelChanged,
            this.propertyViewModel.modelChanged,
            this.propertyViewModel.propertyChanged.pipe(filter((args) => args.propertyName === this.propertyViewModel.bindedValuePropertyName))
        ).pipe(
            this.untilDestroyedThis(),
            takeUntil(this.destroyOnChange$)
        ).subscribe(() => {
            this.checkStatus = this.propertyViewModel.value as boolean;
            this.cd.detectChanges();
        });

        // Rimane in ascolto quando cambiano le property diverse dal valore: isVisible, isEnabled, ecc
        this.propertyViewModel.propertyChanged.pipe(
            this.untilDestroyedThis(),
            takeUntil(this.destroyOnChange$),
            filter((args) => args.propertyName !== this.propertyViewModel.bindedValuePropertyName)
        ).subscribe(() => {
            this.cd.detectChanges();
        });

        this.checkStatus = this.propertyViewModel.value as boolean;
    }

    ngOnInit(): void {
        if (!this.propertyViewModel) { throw new Error('Missing viewModel!'); }
    }

    ngAfterViewInit(): void {
        queueMicrotask(() => {
            this.viewReadyCoordinator.markReady('self');
            this.tryEmitViewReady();
        });
    }

    onBaseViewReady(): void {
        this.viewReadyCoordinator.markReady('child');
        this.tryEmitViewReady();
    }

    private canAccessRenderedChild(): boolean {
        return this.propertyViewModel?.securityAccess == null
            ? this.baseToggleTextBox != null
            : this.baseSecurityTextBox != null;
    }

    private tryEmitViewReady(): void {
        this.viewReadyCoordinator.tryEmit(() => this.canAccessRenderedChild());
    }

    private emitViewReady(): void {
        this.isViewReady = true;
        this.viewReady.emit();
    }

    change(value): void {
        if (!this.isDisabled && this.propertyViewModel.isEnabled) {
            this.checkStatus = value as boolean;

            this.valueChange.emit(this.checkStatus);
            if
                (this.modelUpdate) {
                this.propertyViewModel.value = this.checkStatus;
            }
        }
    }

    showErroTooltip(): void {
        if (this.baseToggleTextBox?.popperError) {
            PopperHelper.show(this.baseToggleTextBox.popperError);
        }
    }
}
